import { useMemo } from "react";
import { LockKeyhole } from "lucide-react";
import { Link } from "react-router";
import { usePermission } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";

/** What a permission opens, in the person's words (shell-ia §14.4): "Your account does not include the Knowledge Bank". */
const WHAT: Record<string, string> = {
  "assistant.use": "econic",
  "knowledge.read": "knowledge",
  "knowledge.govern": "knowledgeManage",
  "knowledge.manage": "knowledgeManage",
  "economics.read": "intelligence",
  "econic.country360.read": "country360",
  "econic.outlook.read": "outlook",
  "econic.policylab.read": "policylab",
  "pages.use": "desks",
  "watchers.use": "tasks",
  "matters.read": "files",
  "trust.read": "trust",
  "platform.accounts.read": "accounts",
  "platform.audit.read": "audit",
  "econic.rollout.manage": "rollout",
};

/**
 * The screen a person reaches without the permission it needs (shell-ia §14.4): it names what is missing, says who grants it, and shows a
 * support reference. It sends no request on the person's behalf. WP-SHELL.
 */
export function ForbiddenPage({ permission }: { permission?: string }) {
  const { t } = useTranslation();
  const first = permission?.split(" · ")[0]?.trim();
  const what = first && WHAT[first] ? t(`assistant:econic.shell.forbidden.${WHAT[first]}`) : t("assistant:econic.shell.forbidden.screen");
  const reference = useMemo(() => `ACC-${Date.now().toString(36).toUpperCase()}`, []);
  const { can } = usePermission();
  const home = can("assistant.use") ? { to: "/", label: t("assistant:econic.shell.error.goToday") } : { to: "/trust", label: t("assistant:econic.shell.nav.trust") };
  return (
    <section className="mx-auto mt-6 w-full max-w-xl" aria-labelledby="econic-forbidden" data-econic-screen="forbidden">
      <div className="ec-card">
        <div className="ec-card-b flex flex-col gap-3 p-5">
          <span className="inline-flex size-10 items-center justify-center rounded-full bg-surface-3 text-ink-2"><LockKeyhole className="size-5" aria-hidden /></span>
          <h1 id="econic-forbidden" className="t-h2" tabIndex={-1}>{t("assistant:econic.shell.error.forbiddenTitle")}</h1>
          <p className="t-body text-ink-2">{t("assistant:econic.shell.error.forbidden", { what })}</p>
          <p className="t-small text-ink-3">{t("assistant:econic.shell.error.forbiddenHint")}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-surface-2 px-3 py-2 text-[12.5px] text-ink-3">
            <dt>{t("assistant:econic.shell.error.reference")}</dt>
            <dd><bdi dir="ltr" className="font-mono text-ink-2">{reference}</bdi></dd>
            {permission ? (
              <>
                <dt>{t("assistant:econic.shell.forbidden.permission")}</dt>
                <dd><bdi dir="ltr" className="font-mono text-ink-2">{permission}</bdi></dd>
              </>
            ) : null}
          </dl>
          <div className="flex flex-wrap gap-2 pt-1">
            <Link to={home.to} className="ec-btn primary sm">{home.label}</Link>
            {home.to !== "/trust" ? <Link to="/trust" className="ec-btn secondary sm">{t("assistant:econic.shell.nav.trust")}</Link> : null}
          </div>
        </div>
      </div>
    </section>
  );
}
